import { createHash } from "crypto";
import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { ILanguage } from "@/view/TiptapArticleBody/components/TranslateDrawer/TranslateDrawer.interface";

interface IUseTranslateTextPayload {
  language: ILanguage;
  text: string;
}

const fetchTranslation = async ({
  text,
  language,
}: IUseTranslateTextPayload) => {
  const url = process.env["NEXT_PUBLIC_VOCALIZER_URL"] || "/vocalizer";

  const response = await fetch(`${url}/translation`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      target_language: language.code,
      text,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to translate text");
  }

  return response.json();
};

const useTranslateText = () => {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);

  const translateText = async (params: IUseTranslateTextPayload) => {
    const hashedText = createHash("md5").update(params.text).digest("hex");
    const key = ["translation", params.language.code, hashedText];

    setIsPending(true);
    setIsError(false);

    try {
      return await queryClient.ensureQueryData({
        queryKey: key,
        queryFn: () => fetchTranslation(params),
      });
    } catch (err) {
      setIsError(true);
      throw err;
    } finally {
      setIsPending(false);
    }
  };

  return { translateText, isPending, isError };
};

export default useTranslateText;
